import { useApolloClient } from '@apollo/client';
import { Divider, Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    CreateAutoplayModuleSettingDocument,
    type CreateAutoplayModuleSettingMutation,
    type CreateAutoplayModuleSettingMutationVariables,
} from '../../../../api/mutations/createAutoplayModuleSetting';
import { AutoplayApiVersion, type AutoplaySettingPayload } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import PasswordField from '../../../../components/fields/PasswordField';
import SelectField from '../../../../components/fields/SelectField';
import useHandleError from '../../../../utilities/useHandleError';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import useValidator from '../../../../utilities/useValidator';
import validator from './validators';

export type AddAutoplaySettingsFormProps = {
    moduleId: string;
    onComplete: () => void;
};

type FormValues = AutoplaySettingPayload;

const initialValues: FormValues = {
    displayName: '',

    apiKey: '',
    apiToken: '',
    apiEndpoint: '',
    apiVersion: AutoplayApiVersion.V3,
    concurrentTasks: 1,
    concurrentSeconds: 1,
};

const AddAutoplaySettingsForm = ({ moduleId, onComplete }: AddAutoplaySettingsFormProps) => {
    const { t } = useTranslation(['moduleDetails', 'autoplayModuleDetails']);

    const apolloClient = useApolloClient();

    const { autoplayVersionOptions } = useSystemOptions();

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('autoplayModuleDetails:autoplaySettingsDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<CreateAutoplayModuleSettingMutation, CreateAutoplayModuleSettingMutationVariables>({
                    mutation: CreateAutoplayModuleSettingDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('autoplayModuleDetails:autoplaySettingsDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [onComplete, t, apolloClient, moduleId]
    );

    const validate = useValidator(validator());

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit, errors }) => (
                <Form
                    id="addAutoplaySettingsForm"
                    layout="vertical"
                    name="addAutoplaySettingsForm"
                    onSubmitCapture={handleSubmit}
                >
                    <Divider>{t('autoplayModuleDetails:dividers.mainSettings')}</Divider>
                    <InputField
                        {...t('autoplayModuleDetails:autoplaySettingsDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />

                    <Divider>{t('autoplayModuleDetails:dividers.autoplaySettings')}</Divider>

                    <PasswordField
                        {...t('autoplayModuleDetails:autoplaySettingsDrawer.fields.apiKey', { returnObjects: true })}
                        name="apiKey"
                        required
                    />

                    <PasswordField
                        {...t('autoplayModuleDetails:autoplaySettingsDrawer.fields.apiToken', { returnObjects: true })}
                        name="apiToken"
                        required
                    />

                    <InputField
                        {...t('autoplayModuleDetails:autoplaySettingsDrawer.fields.apiEndpoint', {
                            returnObjects: true,
                        })}
                        name="apiEndpoint"
                        required
                    />

                    <SelectField
                        {...t('autoplayModuleDetails:autoplaySettingsDrawer.fields.apiVersion', {
                            returnObjects: true,
                        })}
                        name="apiVersion"
                        options={autoplayVersionOptions}
                        required
                        showSearch
                    />

                    <InputNumberField
                        {...t('autoplayModuleDetails:autoplaySettingsDrawer.fields.concurrentTasks', {
                            returnObjects: true,
                        })}
                        name="concurrentTasks"
                        required
                    />

                    <InputNumberField
                        {...t('autoplayModuleDetails:autoplaySettingsDrawer.fields.concurrentSeconds', {
                            returnObjects: true,
                        })}
                        name="concurrentSeconds"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default AddAutoplaySettingsForm;
